/**
 * Word-level diff for the version history panel.
 *
 * Editing a criterion is append-only on the server: the current row is soft-deleted and
 * a new one is inserted under the same group_id. That history was never visible, so an
 * edit silently redefined whatever Insights had been charting. Showing *what changed*
 * between two versions is the point of the panel — a list of timestamps alone doesn't
 * explain why a trend line moved.
 */

export type DiffOp = "same" | "added" | "removed";

export interface DiffToken {
  op: DiffOp;
  text: string;
}

/**
 * Above this many words on either side we stop computing an LCS and report the change
 * wholesale. The table is O(n*m); criteria are capped at 4096 characters, so this only
 * trips on pathological input, but an unbounded allocation here is not worth the risk.
 */
const MAX_TOKENS = 2000;

function tokenize(text: string): string[] {
  return (text ?? "").split(/\s+/).filter(Boolean);
}

/** Merge adjacent tokens sharing an op into single runs, so rendering stays cheap. */
function toRuns(pairs: Array<[DiffOp, string]>): DiffToken[] {
  const runs: DiffToken[] = [];
  for (const [op, word] of pairs) {
    const last = runs[runs.length - 1];
    if (last && last.op === op) {
      last.text += ` ${word}`;
    } else {
      runs.push({ op, text: word });
    }
  }
  return runs;
}

/**
 * Diff two criteria strings at word granularity.
 *
 * Returns runs in reading order: unchanged text, text only in `before` (removed), and
 * text only in `after` (added). Whitespace is normalised to single spaces — this drives
 * a display, not a patch.
 */
export function diffWords(before: string, after: string): DiffToken[] {
  const a = tokenize(before);
  const b = tokenize(after);

  if (a.length === 0 && b.length === 0) return [];
  if (a.length === 0) return [{ op: "added", text: b.join(" ") }];
  if (b.length === 0) return [{ op: "removed", text: a.join(" ") }];

  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    return [
      { op: "removed", text: a.join(" ") },
      { op: "added", text: b.join(" ") },
    ];
  }

  // lcs[i][j] = length of the longest common subsequence of a[i..] and b[j..],
  // stored flat so the whole table is one allocation.
  const width = b.length + 1;
  const lcs = new Int32Array((a.length + 1) * width);
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i * width + j] =
        a[i] === b[j]
          ? lcs[(i + 1) * width + j + 1] + 1
          : Math.max(lcs[(i + 1) * width + j], lcs[i * width + j + 1]);
    }
  }

  const pairs: Array<[DiffOp, string]> = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      pairs.push(["same", a[i]]);
      i++;
      j++;
    } else if (lcs[(i + 1) * width + j] >= lcs[i * width + j + 1]) {
      pairs.push(["removed", a[i]]);
      i++;
    } else {
      pairs.push(["added", b[j]]);
      j++;
    }
  }
  while (i < a.length) pairs.push(["removed", a[i++]]);
  while (j < b.length) pairs.push(["added", b[j++]]);

  return toRuns(pairs);
}

/** True when the two strings differ by more than whitespace. */
export function hasWordChanges(before: string, after: string): boolean {
  return diffWords(before, after).some((token) => token.op !== "same");
}
